/**
 * LoadingNode - Custom ReactFlow node displaying a simple spinner with
 * status text that cycles through loading phases.
 *
 * Styled with a raised surface card (matching ExploreNode) so it looks like
 * a native entity on the canvas.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useEffect, memo, type MemoExoticComponent } from 'react';

import { type NodeProps, type Node } from '@xyflow/react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

export type LoadingNodeData = {
	_tag: 'loading';
};

export type LoadingNodeType = Node<LoadingNodeData, 'loadingNode'>;

const LOADING_PHASES = [
	'Parsing query...',
	'Traversing the graph...',
	'Assembling results...',
	'Almost there...',
];

const PHASE_INTERVAL_MS = 3000;

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.500'),
		paddingRight: token('space.600'),
		paddingBottom: token('space.500'),
		paddingLeft: token('space.600'),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		pointerEvents: 'auto',
		userSelect: 'none',
	},
	text: {
		font: token('font.body'),
		color: token('color.text.subtlest'),
		textAlign: 'center',
	},
});

export const LoadingNode: MemoExoticComponent<(_props: NodeProps<LoadingNodeType>) => JSX.Element> =
	memo((_props: NodeProps<LoadingNodeType>): JSX.Element => {
		const [phaseIndex, setPhaseIndex] = useState(0);

		useEffect(() => {
			const interval = setInterval(() => {
				setPhaseIndex((prev) => Math.min(prev + 1, LOADING_PHASES.length - 1));
			}, PHASE_INTERVAL_MS);

			return () => clearInterval(interval);
		}, []);

		return (
			<Box xcss={styles.container}>
				<Spinner size="large" />
				<Box xcss={styles.text}>{LOADING_PHASES[phaseIndex]}</Box>
			</Box>
		);
	});

LoadingNode.displayName = 'LoadingNode';
